import PessoaBloqueio from "@/Models/Pessoa/PessoaBloqueio";
import RequestUtilitario, { MutationBasicaParam, QueryBasicaParam } from "@/Utils/RequisicaoUtilitario";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { Axios } from "../Axios";
import { queryClient } from "@/App";
import Pagina, { PaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import dayjs from "dayjs";

export function usePessoaBloquear({
    onSuccess,
    ...rest
}:MutationBasicaParam<PessoaBloqueio, AxiosResponse<number>>) {
    return useMutation({
        ...rest,
        mutationFn: (bloqueio) => {
            return Axios.post<number>(`pessoa/bloqueio`, bloqueio);
        },
        onSuccess : (retorno, entrada, context) => {
            queryClient.invalidateQueries({ queryKey: ["Pessoa.Bloqueio", `Pessoa.${entrada.codPessoa}`, `Pessoa.${entrada.codPessoa}.Bloqueio.${entrada.codBloqueioTipo}`] });
            onSuccess?.(retorno, entrada, context);
        }
    });
}

export function usePessoaDesbloquear({
    onSuccess,
    ...rest
}:MutationBasicaParam<PessoaBloqueio, AxiosResponse<void>>) {
    return useMutation({
        ...rest,
        mutationFn: (bloqueio) => {
            return Axios.patch<void>(`pessoa/bloqueio/desbloquear`, bloqueio);
        },
        onSuccess : (retorno, entrada, context) => {
            queryClient.invalidateQueries({ queryKey: ["Pessoa.Bloqueio",`Pessoa.Bloqueio.${entrada.codigo}` ] });
            onSuccess?.(retorno, entrada, context);
        }
    });
}

export function usePessoaBloqueioAtual({
    codPessoa,
    codBloqueioTipo,
    ...rest
}:QueryBasicaParam<AxiosResponse<PessoaBloqueio|undefined>, PessoaBloqueio|undefined, {
    codPessoa : number,
    codBloqueioTipo : number
}>) {
    return useQuery({
        ...rest,
        queryKey : ["Pessoa.Bloqueio", `Pessoa.${codPessoa}.Bloqueio.${codBloqueioTipo}`],
        queryFn : () => Axios.get<PessoaBloqueio|undefined>(`pessoa/${codPessoa}/bloqueio/${codBloqueioTipo}`),
        staleTime : 1000 * 60 * 2.5,
        select(data) {
            return data.data ?? undefined;
        },
    });
}

export type PessoaBloqueioListarPaginaFiltros = {
    paginaInfo?:PaginaInfo,
    bloqueado?:boolean;
    ativo?:boolean;
    pessoaBloqueio?:PessoaBloqueio;
};

export function usePessoaBloqueioListarPagina({
    paginaInfo,
    bloqueado,
    ativo,
    pessoaBloqueio,
    ...rest
}:QueryBasicaParam<AxiosResponse<Pagina<PessoaBloqueio>>, Pagina<PessoaBloqueio>, PessoaBloqueioListarPaginaFiltros>) {
    const queryString = RequestUtilitario.objectToQueryString({
        paginaInfo,
        bloqueado,
        ativo,
        pessoaBloqueio
    });

    return useQuery({
        ...rest,
        queryKey : [ "Pessoa.Bloqueio.Listar", `Pessoa.Bloqueio.Listar.Pagina?${queryString}` ],
        queryFn : () => Axios.get<Pagina<PessoaBloqueio>>(`pessoa/bloqueio/pagina?${queryString}`),
        select(data) {
            data.data.registros.forEach(m => {
                m.dataCriacao = new Date(m.dataCriacao as any);
                m.dataBloqueio = new Date(m.dataBloqueio as any);
                m.dataLiberacao = !!m.dataLiberacao ? new Date(m.dataLiberacao as any) : undefined;
            });

            return data.data;
        },
        staleTime: 1000 * 60 * 0.25
    });
}

export function usePessoaBloquioBuscar({
    codigo,
    ativo,
    bloqueado,
    ...rest
}:QueryBasicaParam<AxiosResponse<PessoaBloqueio|null>, PessoaBloqueio|null, { codigo : number, ativo ?: boolean, bloqueado ?: boolean }>) {
    return useQuery({
        ...rest,
        queryKey : [ "Pessoa.Bloqueio", `Pessoa.Bloqueio.${codigo}`, `Pessoa.Bloqueio.${codigo}?ativo=${ativo ?? ''}&bloqueado=${bloqueado ?? ''}` ],
        queryFn : () => Axios.get<PessoaBloqueio|null>(`pessoa/bloqueio/${codigo}?ativo=${ativo ?? ''}&bloqueado=${bloqueado ?? ''}`),
        select(data) {
            if (data.data)
            {
                data.data.dataCriacao = dayjs(new Date(data.data.dataCriacao as any));
                data.data.dataBloqueio = dayjs(new Date(data.data.dataBloqueio as any));
                data.data.dataLiberacao = !!data.data?.dataLiberacao ? dayjs(new Date(data.data.dataLiberacao as any)) : undefined;
            }
            return data.data ?? null;
        },
        staleTime: 1000 * 60 * 0.25
    });
}
